import { cn } from '@wend/ui';
import { CircleAlert, CircleCheck, Info } from 'lucide-react';
import type { ReactNode } from 'react';

export type FormAlertTone = 'error' | 'success' | 'info';

const TONES: Readonly<Record<FormAlertTone, { root: string; icon: string; Icon: typeof Info }>> = {
  error: { root: 'border-bad/40 bg-bad/[0.08]', icon: 'text-bad', Icon: CircleAlert },
  success: { root: 'border-good/40 bg-good/[0.08]', icon: 'text-good', Icon: CircleCheck },
  info: { root: 'border-info/35 bg-info/[0.07]', icon: 'text-info', Icon: Info },
};

export interface FormAlertProps {
  tone: FormAlertTone;
  children: ReactNode;
  /**
   * Correlates the failure with a server log line. Shown only when the API sent
   * one, and only ever as an opaque id.
   */
  requestId?: string;
  className?: string;
}

/**
 * A form-level message: the thing that went wrong for the submission as a
 * whole, as opposed to a field's own error.
 *
 * `role="alert"` announces it the moment it is inserted, which is what makes a
 * failed sign-in audible to a screen reader without a permanent live region.
 */
export function FormAlert({ tone, children, requestId, className }: FormAlertProps) {
  const { root, icon, Icon } = TONES[tone];

  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-2.5 rounded-base border px-3.5 py-3 text-[0.86rem] leading-snug',
        root,
        className,
      )}
    >
      <Icon aria-hidden="true" className={cn('mt-0.5 size-4 shrink-0', icon)} />
      <div className="min-w-0 flex-1">
        <p className="text-text">{children}</p>
        {requestId !== undefined && (
          <p className="mt-1.5 font-mono text-[0.68rem] uppercase tracking-[0.1em] text-muted">
            Reference {requestId}
          </p>
        )}
      </div>
    </div>
  );
}
